import { useTranslation } from 'react-i18next';
import { Badge } from '@/components/ui/badge';
import { ArrowTopRightOnSquareIcon } from '@heroicons/react/24/outline';
import type { StripeTransaction } from '@/types/stripe';
import { formatStripeAmount, formatStripeDate } from '@/utils/stripeFormatters';

const TRANSACTION_STATUS_CLASS: Record<string, string> = {
  succeeded: 'bg-green-100 text-green-800 border-green-200',
  requires_payment_method: 'bg-amber-100 text-amber-800 border-amber-200',
  canceled: 'bg-red-100 text-red-800 border-red-200',
};

const TRANSACTION_STATUS_LABEL_KEY: Record<string, string> = {
  succeeded: 'transactionsTable.statusSucceeded',
  requires_payment_method: 'transactionsTable.statusRequiresMethod',
  canceled: 'transactionsTable.statusCanceled',
};

interface StripeTransactionsTableProps {
  transactions?: StripeTransaction[];
}

export function StripeTransactionsTable({ transactions }: StripeTransactionsTableProps) {
  const { t } = useTranslation('billing');

  function getTransactionStatusBadge(status: string | null | undefined) {
    if (!status) return <span className="text-gray-500">—</span>;
    const key = status.toLowerCase();
    const className = TRANSACTION_STATUS_CLASS[key] ?? 'bg-gray-100 text-gray-800 border-gray-200';
    const labelKey = TRANSACTION_STATUS_LABEL_KEY[key];
    const label = labelKey ? t(labelKey) : status;
    return <Badge variant="outline" className={className}>{label}</Badge>;
  }

  if (!transactions || transactions.length === 0) {
    return <div className="text-sm text-gray-600">{t('transactionsTable.noData')}</div>;
  }

  return (
    <div className="space-y-3">
      <div className="space-y-2 sm:hidden">
        {transactions.map((tx) => (
          <div key={tx.id} className="rounded-lg border border-gray-200 bg-white p-3">
            <div className="font-medium text-gray-900 break-all text-xs">{tx.id}</div>
            <div className="flex items-center gap-2 mt-1">
              {getTransactionStatusBadge(tx.status)}
              <span className="text-xs text-gray-500">{formatStripeDate(tx.created, { withTime: true })}</span>
            </div>
            <div className="text-sm text-gray-700 mt-1">{formatStripeAmount(tx.amount, tx.currency)}</div>
            {tx.description && <div className="text-xs text-gray-600 mt-1">{tx.description}</div>}
            <div className="flex flex-wrap gap-3 mt-2">
              {tx.charge_id && (
                <a href={`https://dashboard.stripe.com/payments/${tx.charge_id}`} target="_blank" rel="noopener noreferrer" className="text-xs text-green-600 hover:text-green-700 inline-flex items-center gap-1">
                  {t('transactionsTable.viewPayment')} <ArrowTopRightOnSquareIcon width={12} height={12} />
                </a>
              )}
              {tx.invoice_id && (
                <a href={`https://dashboard.stripe.com/invoices/${tx.invoice_id}`} target="_blank" rel="noopener noreferrer" className="text-xs text-green-600 hover:text-green-700 inline-flex items-center gap-1">
                  {t('transactionsTable.viewInvoice')} <ArrowTopRightOnSquareIcon width={12} height={12} />
                </a>
              )}
            </div>
          </div>
        ))}
      </div>

      <div className="hidden sm:block overflow-x-auto rounded-lg border border-gray-200">
        <table className="min-w-full divide-y divide-gray-200 text-sm">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-3 py-2 text-left font-medium text-gray-700">{t('transactionsTable.date')}</th>
              <th className="px-3 py-2 text-left font-medium text-gray-700">{t('transactionsTable.status')}</th>
              <th className="px-3 py-2 text-left font-medium text-gray-700">{t('transactionsTable.amount')}</th>
              <th className="px-3 py-2 text-left font-medium text-gray-700">{t('transactionsTable.method')}</th>
              <th className="px-3 py-2 text-left font-medium text-gray-700">{t('transactionsTable.invoice')}</th>
              <th className="px-3 py-2 text-left font-medium text-gray-700">{t('transactionsTable.charge')}</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100 bg-white">
            {transactions.map((tx) => (
              <tr key={tx.id}>
                <td className="px-3 py-2 text-gray-700 whitespace-nowrap">{formatStripeDate(tx.created, { withTime: true })}</td>
                <td className="px-3 py-2">{getTransactionStatusBadge(tx.status)}</td>
                <td className="px-3 py-2 text-gray-700 whitespace-nowrap">{formatStripeAmount(tx.amount, tx.currency)}</td>
                <td className="px-3 py-2 text-gray-700">{tx.payment_method || t('transactionsTable.empty')}</td>
                <td className="px-3 py-2">
                  {tx.invoice_id ? (
                    <a href={`https://dashboard.stripe.com/invoices/${tx.invoice_id}`} target="_blank" rel="noopener noreferrer" className="text-xs text-green-600 hover:text-green-700 inline-flex items-center gap-1">
                      {tx.invoice_id} <ArrowTopRightOnSquareIcon width={12} height={12} />
                    </a>
                  ) : (
                    <span className="text-gray-500">{t('transactionsTable.empty')}</span>
                  )}
                </td>
                <td className="px-3 py-2">
                  {tx.charge_id ? (
                    <a href={`https://dashboard.stripe.com/payments/${tx.charge_id}`} target="_blank" rel="noopener noreferrer" className="text-xs text-green-600 hover:text-green-700 inline-flex items-center gap-1">
                      {tx.charge_id} <ArrowTopRightOnSquareIcon width={12} height={12} />
                    </a>
                  ) : (
                    <span className="text-gray-500">{t('transactionsTable.empty')}</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

